import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import MindElixir from 'mind-elixir';
import type { MindElixirInstance, MindElixirData, NodeObj } from 'mind-elixir';
import 'mind-elixir/style.css';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import {
  Loader2,
  Share2,
  LayoutGrid,
  Undo2,
  Redo2,
  Download,
  Users,
  WifiOff,
  AlertTriangle,
  Plus,
  Minus,
  Trash2,
  Type,
  Copy,
  Palette,
  CheckCircle2,
  Save,
  AlertCircle,
  Clock,
  Network,
} from 'lucide-react';
import { docsApi } from '../api/docs';
import { usersApi } from '../api/users';
import { getWsToken } from '../api/auth';
import { toast } from '../components/Toast';
import { getCollaboratorColor, getInitials } from '../lib/utils';

/** 直连 Worker 的 WebSocket URL */
const COLLAB_WS_BASE = 'wss://xs.xiniu.qzz.io/api/docs';

/** MindElixir 右键菜单中文语言包（内联避免 Vite tree-shake 丢失） */
const ZH_CN = {
  addChild: '插入子节点',
  addParent: '插入父节点',
  addSibling: '插入同级节点',
  removeNode: '删除节点',
  focus: '专注',
  cancelFocus: '取消专注',
  moveUp: '上移',
  moveDown: '下移',
  link: '连接',
  linkBidirectional: '双向连接',
  clickTips: '请点击目标节点',
  summary: '摘要',
} as const;

/** 自动保存间隔（毫秒） */
const AUTO_SAVE_INTERVAL = 30_000;

/** 变更后防抖保存延迟（毫秒） */
const DEBOUNCE_SAVE_DELAY = 1_000;

/** 最大保存重试次数 */
const MAX_SAVE_RETRIES = 3;

/** 预设节点颜色 */
const NODE_COLORS = [
  { label: '默认', value: '' },
  { label: '红色', value: '#FEE2E2' },
  { label: '橙色', value: '#FED7AA' },
  { label: '黄色', value: '#FEF08A' },
  { label: '绿色', value: '#BBF7D0' },
  { label: '蓝色', value: '#BFDBFE' },
  { label: '紫色', value: '#E9D5FF' },
  { label: '粉色', value: '#FBCFE8' },
  { label: '灰色', value: '#E5E7EB' },
];

type CollabStatus = 'connecting' | 'connected' | 'offline' | 'error';

type SaveStatus = 'saved' | 'saving' | 'error' | 'unsaved';

interface AwarenessUser {
  name: string;
  color: string;
  clientId: number;
}

/** 默认思维导图数据 */
function defaultMindmapData(): MindElixirData {
  return {
    nodeData: {
      id: 'root',
      topic: '中心主题',
      children: [
        {
          id: 'child1',
          topic: '分支主题 1',
          children: [],
        },
        {
          id: 'child2',
          topic: '分支主题 2',
          children: [],
        },
      ],
    },
  };
}

export function MindmapEditorPage() {
  const { mapId = '' } = useParams<{ mapId: string }>();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const meRef = useRef<MindElixirInstance | null>(null);
  const [title, setTitle] = useState('未命名导图');
  const [titleDirty, setTitleDirty] = useState(false);
  const [nodeCount, setNodeCount] = useState(0);
  const [collabStatus, setCollabStatus] = useState<CollabStatus>('connecting');
  const [collaborators, setCollaborators] = useState<AwarenessUser[]>([]);
  const [showWsWarning, setShowWsWarning] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [zoomPercent, setZoomPercent] = useState(100);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const titleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoSaveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isApplyingRemoteRef = useRef(false);
  const yMapRef = useRef<Y.Map<string> | null>(null);
  const hasLoadedInitialDataRef = useRef(false);
  const retryCountRef = useRef(0);
  const colorPickerRef = useRef<HTMLDivElement | null>(null);

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: usersApi.me,
  });

  const { data: meta, isLoading } = useQuery({
    queryKey: ['mindmap', mapId],
    queryFn: () => docsApi.get(mapId),
    enabled: !!mapId,
  });

  useEffect(() => {
    if (meta) setTitle(meta.title || '未命名导图');
  }, [meta]);

  // 创建 Yjs 文档 + WebSocket Provider
  const provider = useMemo(() => {
    if (!mapId) return null;
    const token = getWsToken();
    const roomname = token
      ? `${mapId}/collab?token=${encodeURIComponent(token)}`
      : `${mapId}/collab`;
    return new WebsocketProvider(COLLAB_WS_BASE, roomname, new Y.Doc(), {
      connect: true,
    });
  }, [mapId]);

  // 获取 Yjs Map 引用
  const yMap = useMemo(() => {
    if (!provider) return null;
    const map = provider.doc.getMap<string>('mindmap');
    yMapRef.current = map;
    return map;
  }, [provider]);

  // 监听连接状态
  useEffect(() => {
    if (!provider) return;
    const onStatus = (event: { status: 'connecting' | 'connected' | 'disconnected' }) => {
      if (event.status === 'connected') {
        setCollabStatus('connected');
        setShowWsWarning(false);
      } else if (event.status === 'connecting') {
        setCollabStatus('connecting');
      } else {
        setCollabStatus('offline');
      }
    };
    const onAwareness = () => {
      const states = Array.from(provider.awareness.getStates().entries() as Iterable<[number, unknown]>);
      const list: AwarenessUser[] = states
        .map(([clientId, state]) => {
          const u = (state as Record<string, unknown>)?.user as { name?: string; color?: string } | undefined;
          if (!u) return null;
          return {
            clientId,
            name: u.name ?? '匿名',
            color: u.color ?? '#6B7280',
          };
        })
        .filter((x): x is AwarenessUser => x !== null);
      setCollaborators(list);
    };
    provider.on('status', onStatus);
    provider.awareness.on('change', onAwareness);

    const warnTimer = setTimeout(() => {
      if (provider.ws?.readyState !== WebSocket.OPEN) {
        setShowWsWarning(true);
        setCollabStatus((s) => (s === 'connected' ? s : 'offline'));
      }
    }, 5000);

    return () => {
      provider.off('status', onStatus);
      provider.awareness.off('change', onAwareness);
      clearTimeout(warnTimer);
      provider.destroy();
    };
  }, [provider]);

  // 设置 awareness user
  useEffect(() => {
    if (!provider || !me) return;
    provider.awareness.setLocalStateField('user', {
      name: me.nickname || me.email.split('@')[0],
      color: getCollaboratorColor(me.id),
    });
  }, [provider, me]);

  // 同步 MindElixir → Yjs
  const syncToYjs = useCallback(() => {
    const inst = meRef.current;
    if (!inst || !yMapRef.current) return;
    if (isApplyingRemoteRef.current) return;
    try {
      const data = inst.getData();
      // 清理 parent 引用避免循环引用
      const cleaned = JSON.parse(JSON.stringify(data));
      yMapRef.current.set('data', JSON.stringify(cleaned));
    } catch {
      // 序列化失败时静默降级
    }
  }, []);

  // 从 Yjs 加载数据到 MindElixir
  const applyYjsData = useCallback((dataStr: string | undefined) => {
    const inst = meRef.current;
    if (!inst || !dataStr) return;
    try {
      const parsed = JSON.parse(dataStr) as MindElixirData;
      if (!parsed?.nodeData) return;
      isApplyingRemoteRef.current = true;
      inst.refresh(parsed);
      inst.clearHistory?.();
      setNodeCount(countNodes(inst.nodeData));
      isApplyingRemoteRef.current = false;
    } catch {
      isApplyingRemoteRef.current = false;
    }
  }, []);

  // 监听 Yjs 更新（来自远程）
  useEffect(() => {
    if (!yMap) return;

    const observer = (event: Y.YMapEvent<string>, _transaction: Y.Transaction) => {
      // 只处理远程更新（本地更新会通过 MindElixir operation 同步）
      if (_transaction.local) return;
      const changedKeys = Array.from(event.changes.keys.entries());
      const dataChanged = changedKeys.some(([key]) => key === 'data');
      if (dataChanged) {
        const dataStr = yMap.get('data');
        applyYjsData(dataStr);
      }
    };

    yMap.observe(observer);

    // 初始同步完成后检查是否有数据
    const onSync = (isSynced: boolean) => {
      if (isSynced && !hasLoadedInitialDataRef.current) {
        hasLoadedInitialDataRef.current = true;
        const dataStr = yMap.get('data');
        if (dataStr) {
          applyYjsData(dataStr);
        } else {
          // Yjs 无数据，尝试从 REST API 加载（离线回退）
          loadFromRestApi();
        }
      }
    };

    provider?.on('sync', onSync);

    // 如果 provider 已经同步完成，立即检查
    if (provider?.synced) {
      hasLoadedInitialDataRef.current = true;
      const dataStr = yMap.get('data');
      if (dataStr) {
        applyYjsData(dataStr);
      } else {
        loadFromRestApi();
      }
    }

    return () => {
      yMap.unobserve(observer);
      provider?.off('sync', onSync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yMap, provider, applyYjsData]);

  // 从 REST API 加载数据（离线回退）
  const loadFromRestApi = useCallback(async () => {
    if (!mapId) return;
    try {
      const res = await docsApi.getMindmapData(mapId);
      if (res?.data) {
        const parsed = JSON.parse(res.data) as MindElixirData;
        if (parsed?.nodeData) {
          const inst = meRef.current;
          if (inst) {
            isApplyingRemoteRef.current = true;
            inst.refresh(parsed);
            inst.clearHistory?.();
            setNodeCount(countNodes(inst.nodeData));
            isApplyingRemoteRef.current = false;
          }
        }
      }
    } catch {
      // REST API 不可用时静默降级，使用默认数据
    }
  }, [mapId]);

  // 保存到 REST API（带重试）
  const saveToRestApi = useCallback(async (): Promise<boolean> => {
    const inst = meRef.current;
    if (!inst || !mapId) return false;
    try {
      setSaveStatus('saving');
      const data = inst.getData();
      const cleaned = JSON.parse(JSON.stringify(data));
      await docsApi.saveMindmapData(mapId, JSON.stringify(cleaned));
      setSaveStatus('saved');
      retryCountRef.current = 0;
      return true;
    } catch (err) {
      retryCountRef.current++;
      if (retryCountRef.current <= MAX_SAVE_RETRIES) {
        // 指数退避重试
        const delay = Math.min(1000 * Math.pow(2, retryCountRef.current - 1), 10000);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return saveToRestApi();
      }
      setSaveStatus('error');
      toast.error(err instanceof Error ? err.message : '保存失败', '自动保存失败');
      return false;
    }
  }, [mapId]);

  // 防抖保存 & 同步到 Yjs
  const debouncedSave = useCallback(() => {
    syncToYjs();
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveToRestApi();
    }, DEBOUNCE_SAVE_DELAY);
  }, [syncToYjs, saveToRestApi]);

  // 自动保存定时器
  useEffect(() => {
    autoSaveIntervalRef.current = setInterval(() => {
      saveToRestApi();
    }, AUTO_SAVE_INTERVAL);
    return () => {
      if (autoSaveIntervalRef.current) clearInterval(autoSaveIntervalRef.current);
    };
  }, [saveToRestApi]);

  // 清理保存定时器
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // 初始化 MindElixir
  useEffect(() => {
    if (isLoading) return;  // meta 还没加载完，不渲染 container，跳过
    if (!containerRef.current) {
      console.error('[MindElixir] containerRef is null after loading, init aborted');
      return;
    }
    console.log('[MindElixir] containerRef:', containerRef.current, 'size:', containerRef.current.offsetWidth, 'x', containerRef.current.offsetHeight);
    const data = defaultMindmapData();
    let instance: MindElixirInstance | null = null;
    try {
      instance = new MindElixir({
        el: containerRef.current,
        direction: 1,
        editable: true,
        // 右键上下文菜单使用中文
        contextMenu: { locale: ZH_CN },
        allowUndo: true,
        overflowHidden: false,
      });
      console.log('[MindElixir] instance created:', instance);
      instance.init(data);
      console.log('[MindElixir] init done, nodeData:', instance.nodeData);
      meRef.current = instance;
      setNodeCount(countNodes(instance.nodeData));
    } catch (err) {
      console.error('[MindElixir] init FAILED:', err);
      return;
    }

    const refreshCount = () => {
      if (meRef.current) setNodeCount(countNodes(meRef.current.nodeData));
    };

    // 监听操作事件 → 同步到 Yjs + 触发防抖保存
    const onOperation = () => {
      if (isApplyingRemoteRef.current) return;
      refreshCount();
      setSaveStatus('unsaved');
      debouncedSave();
    };

    const onScale = (scale: number) => {
      setZoomPercent(Math.round(scale * 100));
    };

    instance.bus.addListener('operation', onOperation);
    instance.bus.addListener('scale', onScale);

    return () => {
      instance.bus.removeListener('operation', onOperation);
      instance.bus.removeListener('scale', onScale);
      instance.destroy();
      meRef.current = null;
    };
  }, [debouncedSave, isLoading]);

  // 点击外部关闭颜色选择器
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target as Node)) {
        setShowColorPicker(false);
      }
    };
    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColorPicker]);

  // 标题防抖保存
  function handleTitleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    setTitle(next);
    setTitleDirty(true);
    if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
    titleTimerRef.current = setTimeout(async () => {
      try {
        await docsApi.update(mapId, { title: next.trim() || '未命名导图' });
        setTitleDirty(false);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : '保存失败', '标题保存失败');
      }
    }, 500);
  }

  useEffect(() => {
    return () => {
      if (titleTimerRef.current) clearTimeout(titleTimerRef.current);
    };
  }, []);

  // 获取当前选中节点
  function getSelectedNode(): NodeObj | null {
    const inst = meRef.current;
    if (!inst) return null;
    const topic = inst.currentNode;
    if (!topic) {
      toast.warning('请先选中一个节点');
      return null;
    }
    return inst.getObjById(topic.id, inst.nodeData);
  }

  // 确保有节点被选中：没有选中时默认选中根节点
  const ensureSelection = useCallback((): boolean => {
    const inst = meRef.current;
    if (!inst) return false;
    if (inst.currentNode) return true;
    // 没有选中节点时，默认选中根节点
    const rootTopic = inst.findEle(inst.nodeData.id);
    if (rootTopic) {
      inst.selectNode(rootTopic);
      return true;
    }
    toast.warning('请先选中一个节点');
    return false;
  }, []);

  // 添加子节点
  const handleAddChild = useCallback(async () => {
    const inst = meRef.current;
    if (!inst) return;
    if (!ensureSelection()) return;
    try {
      // 不传 node 参数：让 MindElixir 内部 generateNewObj + editTopic
      // 传自定义 node 会导致 editTopic 被跳过，用户看不到编辑框
      await inst.addChild();
    } catch (err) {
      console.error('[addChild]', err);
      toast.error('添加子节点失败');
    }
  }, [ensureSelection]);

  // 添加兄弟节点
  const handleAddSibling = useCallback(async () => {
    const inst = meRef.current;
    if (!inst) return;
    if (!ensureSelection()) return;
    try {
      await inst.insertSibling('after');
    } catch (err) {
      console.error('[insertSibling]', err);
      toast.error('添加兄弟节点失败');
    }
  }, [ensureSelection]);

  // 删除选中节点
  const handleDeleteNode = useCallback(async () => {
    const inst = meRef.current;
    if (!inst) return;
    const topic = inst.currentNode;
    if (!topic) {
      toast.warning('请先选中一个节点');
      return;
    }
    // 不允许删除根节点
    if (!topic.nodeObj.parent) {
      toast.warning('不能删除根节点');
      return;
    }
    try {
      await inst.removeNodes([topic]);
    } catch (err) {
      console.error('[removeNodes]', err);
      toast.error('删除节点失败');
    }
  }, []);

  // 设置节点颜色
  const handleSetColor = useCallback(
    async (color: string) => {
      const inst = meRef.current;
      if (!inst) return;
      const topic = inst.currentNode;
      if (!topic) {
        toast.warning('请先选中一个节点');
        return;
      }
      try {
        if (color) {
          await inst.reshapeNode(topic, { style: { background: color } });
        } else {
          await inst.reshapeNode(topic, { style: { background: '' } });
        }
        setShowColorPicker(false);
      } catch {
        toast.error('设置颜色失败');
      }
    },
    [],
  );

  // 复制节点文本
  const handleCopyText = useCallback(() => {
    const node = getSelectedNode();
    if (!node) return;
    navigator.clipboard.writeText(node.topic).then(
      () => toast.success('已复制节点文本'),
      () => toast.error('复制失败'),
    );
  }, []);

  // 布局
  const handleLayout = useCallback(() => {
    meRef.current?.layout();
    toast.info('已重新布局');
  }, []);

  // 撤销/重做
  const handleUndo = useCallback(() => {
    meRef.current?.undo();
  }, []);

  const handleRedo = useCallback(() => {
    meRef.current?.redo();
  }, []);

  // 缩放
  const handleZoomIn = useCallback(() => {
    const inst = meRef.current;
    if (!inst) return;
    const newScale = Math.min((inst.scaleVal || 1) + 0.1, 2);
    inst.scale(newScale);
  }, []);

  const handleZoomOut = useCallback(() => {
    const inst = meRef.current;
    if (!inst) return;
    const newScale = Math.max((inst.scaleVal || 1) - 0.1, 0.3);
    inst.scale(newScale);
  }, []);

  const handleResetZoom = useCallback(() => {
    const inst = meRef.current;
    if (!inst) return;
    inst.scale(1);
  }, []);

  // 导出 SVG
  const handleExport = useCallback(() => {
    const inst = meRef.current;
    if (!inst) return;
    try {
      const blob = inst.exportSvg?.();
      if (!blob) {
        toast.warning('导出能力暂不可用');
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'mindmap'}.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('已导出 SVG');
    } catch {
      toast.warning('导出失败');
    }
  }, [title]);

  // 手动保存
  const handleManualSave = useCallback(async () => {
    await saveToRestApi();
    if (saveStatus === 'saved') {
      toast.success('已保存');
    }
  }, [saveToRestApi, saveStatus]);

  // 配置
  const statusConfig = {
    connecting: { color: 'bg-warning', text: '连接中' },
    connected: { color: 'bg-success', text: '已同步' },
    offline: { color: 'bg-danger', text: '离线模式' },
    error: { color: 'bg-danger', text: '连接错误' },
  } as const;
  const status = statusConfig[collabStatus];

  const saveStatusConfig: Record<SaveStatus, { icon: typeof Save; color: string; text: string }> = {
    saved: { icon: CheckCircle2, color: 'text-success', text: '已保存' },
    saving: { icon: Clock, color: 'text-warning', text: '保存中…' },
    error: { icon: AlertCircle, color: 'text-danger', text: '保存失败' },
    unsaved: { icon: Save, color: 'text-text-tertiary', text: '未保存' },
  };
  const saveInfo = saveStatusConfig[saveStatus];
  const SaveIcon = saveInfo.icon;

  const keyboardShortcuts = [
    { key: 'Tab', desc: '添加子节点' },
    { key: 'Enter', desc: '添加兄弟节点' },
    { key: 'Delete', desc: '删除节点' },
    { key: 'Ctrl+Z', desc: '撤销' },
    { key: 'Ctrl+Y', desc: '重做' },
    { key: 'Ctrl+S', desc: '手动保存' },
    { key: 'Alt+Shift+F', desc: '自动布局' },
    { key: '空格+拖动', desc: '平移画布' },
    { key: '滚轮', desc: '缩放画布' },
    { key: '拖动节点', desc: '移动节点到其他位置' },
    { key: '右键', desc: '上下文菜单' },
    { key: '?', desc: '查看快捷键' },
  ];

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* 顶部 bar */}
      <div className="flex items-center justify-between border-b border-border bg-bg-surface px-6 py-3">
        <div className="flex flex-1 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-accent-50 to-accent-100 shadow-xs ring-1 ring-accent/20">
            {meta?.emoji ? (
              <span aria-hidden className="text-lg">{meta.emoji}</span>
            ) : (
              <Network className="h-5 w-5 text-accent" />
            )}
          </div>
          <input
            value={title}
            onChange={handleTitleChange}
            placeholder="未命名导图"
            className="flex-1 bg-transparent text-lg font-semibold focus:outline-none"
            aria-label="导图标题"
          />
          {titleDirty && <span className="text-xs text-text-tertiary">保存中…</span>}
        </div>

        <div className="flex items-center gap-3">
          {/* 保存状态 */}
          <span
            className="flex items-center gap-1 text-xs"
            title={saveInfo.text}
          >
            <SaveIcon className={`h-3.5 w-3.5 ${saveInfo.color}`} />
            <span className={saveInfo.color}>{saveInfo.text}</span>
          </span>

          {/* 协作者头像 stack */}
          {collaborators.length > 0 && (
            <div className="flex items-center -space-x-2" aria-label={`当前 ${collaborators.length} 位协作者`}>
              {collaborators.slice(0, 5).map((c) => (
                <span
                  key={c.clientId}
                  className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-bg-surface text-[10px] font-medium text-white"
                  style={{ backgroundColor: c.color }}
                  title={c.name}
                >
                  {getInitials(c.name)}
                </span>
              ))}
              {collaborators.length > 5 && (
                <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-bg-surface bg-bg-subtle text-[10px] text-text-secondary">
                  +{collaborators.length - 5}
                </span>
              )}
            </div>
          )}

          <span className="flex items-center gap-1.5 text-xs text-text-tertiary" title={status.text}>
            <span
              className={`h-1.5 w-1.5 rounded-full ${status.color} ${
                collabStatus === 'connecting' ? 'animate-pulse' : ''
              }`}
            />
            {status.text}
          </span>

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => toast.info('分享功能即将上线')}
          >
            <Share2 className="h-3.5 w-3.5" /> 分享
          </button>
        </div>
      </div>

      {/* WS 警告条 */}
      {showWsWarning && collabStatus !== 'connected' && (
        <div className="flex items-center gap-2 border-b border-warning/30 bg-[#fffbeb] px-6 py-2 text-xs text-warning">
          <AlertTriangle className="h-3.5 w-3.5" />
          协同服务连接失败，当前为本地编辑模式。
          <button
            type="button"
            className="ml-auto underline"
            onClick={() => {
              provider?.connect();
              setCollabStatus('connecting');
            }}
          >
            重试
          </button>
        </div>
      )}

      {/* 工具栏 */}
      <div className="flex items-center gap-1 border-b border-border bg-bg-surface px-6 py-2">
        {/* 节点操作 */}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={handleAddChild}
          title="添加子节点 (Tab)"
        >
          <Plus className="h-3.5 w-3.5" /> 子节点
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={handleAddSibling}
          title="添加兄弟节点 (Enter)"
        >
          <Type className="h-3.5 w-3.5" /> 兄弟节点
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={handleDeleteNode}
          title="删除节点 (Delete)"
        >
          <Trash2 className="h-3.5 w-3.5" /> 删除
        </button>
        <span className="mx-1 h-4 w-px bg-border" aria-hidden />

        {/* 样式 */}
        <div className="relative" ref={colorPickerRef}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setShowColorPicker(!showColorPicker)}
            title="节点颜色"
          >
            <Palette className="h-3.5 w-3.5" /> 颜色
          </button>
          {showColorPicker && (
            <div className="absolute left-0 top-full z-50 mt-1 rounded-lg border border-border bg-bg-surface p-2 shadow-lg">
              <div className="mb-1 text-xs text-text-secondary">选择颜色</div>
              <div className="flex flex-wrap gap-1">
                {NODE_COLORS.map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    className="h-6 w-6 rounded border border-border transition-transform hover:scale-110"
                    style={{ backgroundColor: c.value || 'transparent' }}
                    title={c.label}
                    onClick={() => handleSetColor(c.value)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={handleCopyText}
          title="复制节点文本"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>

        <span className="mx-1 h-4 w-px bg-border" aria-hidden />

        {/* 布局/撤销/重做 */}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={handleLayout}
          title="自动布局 (Alt+Shift+F)"
        >
          <LayoutGrid className="h-3.5 w-3.5" /> 布局
        </button>
        <span className="mx-1 h-4 w-px bg-border" aria-hidden />
        <button type="button" className="btn btn-ghost btn-sm" onClick={handleUndo} title="撤销 (Ctrl+Z)">
          <Undo2 className="h-3.5 w-3.5" />
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={handleRedo} title="重做 (Ctrl+Y)">
          <Redo2 className="h-3.5 w-3.5" />
        </button>
        <span className="mx-1 h-4 w-px bg-border" aria-hidden />

        {/* 导出 */}
        <button type="button" className="btn btn-ghost btn-sm" onClick={handleExport} title="导出 SVG">
          <Download className="h-3.5 w-3.5" /> 导出
        </button>

        {/* 手动保存 */}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={handleManualSave}
          title="手动保存 (Ctrl+S)"
        >
          <Save className="h-3.5 w-3.5" />
        </button>

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={handleZoomOut}
            title="缩小"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm min-w-[56px] text-xs font-medium"
            onClick={handleResetZoom}
            title="重置缩放"
          >
            {zoomPercent}%
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={handleZoomIn}
            title="放大"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* 画布 */}
      <div className="relative flex-1 overflow-hidden bg-bg-canvas">
        <div ref={containerRef} className="h-full w-full" />
      </div>

      {/* 状态栏 */}
      <div className="flex items-center justify-between border-t border-border bg-bg-surface px-6 py-2 text-xs text-text-tertiary">
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {collaborators.length} 位在线
          </span>
          <span className="flex items-center gap-1">
            {nodeCount} 节点
          </span>
          <span className="text-text-quaternary">·</span>
          <span className="text-text-quaternary">{zoomPercent}%</span>
          {collabStatus === 'offline' && (
            <span className="flex items-center gap-1 text-danger">
              <WifiOff className="h-3 w-3" /> 离线
            </span>
          )}
          {saveStatus === 'error' && (
            <button
              type="button"
              className="flex items-center gap-1 text-danger underline"
              onClick={handleManualSave}
            >
              <AlertCircle className="h-3 w-3" /> 保存失败，点击重试
            </button>
          )}
        </span>
        <span className="flex items-center gap-2">
          <button
            type="button"
            className="underline hover:text-text-secondary"
            onClick={() => setShowShortcuts(!showShortcuts)}
          >
            {showShortcuts ? '隐藏快捷键' : '快捷键 ?'}
          </button>
        </span>
      </div>

      {/* 快捷键面板 */}
      {showShortcuts && (
        <div className="border-t border-border bg-bg-surface px-6 py-3">
          <div className="flex flex-wrap gap-x-8 gap-y-1.5">
            {keyboardShortcuts.map((s) => (
              <span key={s.key} className="flex items-center gap-2 text-xs">
                <kbd className="rounded border border-border bg-bg-subtle px-1.5 py-0.5 font-mono text-[10px]">
                  {s.key}
                </kbd>
                <span className="text-text-secondary">{s.desc}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** 递归统计节点数 */
function countNodes(node: NodeObj | undefined): number {
  if (!node) return 0;
  let count = 1;
  const children = node.children ?? [];
  for (const child of children) {
    count += countNodes(child);
  }
  return count;
}